/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.22/esri/copyright.txt for details.
*/
import { clamp as n } from "../../core/mathUtils.js";
import { isSome as t } from "../../core/maybe.js";
import { g as r, k as c, f as o, n as e, d as u, c as i, a as f, b as s } from "../../chunks/vec3.js";
import { c as a } from "../../chunks/vec3f64.js";
import { g } from "../../chunks/sphere.js";
import { sv4d as m, sv3d as h } from "./vectorStacks.js";

function p() {
  var n = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : X;
  return [n[0], n[1], n[2], n[3]];
}

function M(n, t, r, c) {
  return j(n, t, r, c, m.get());
}

function b(n) {
  var t = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : p();
  return j(n[0], n[1], n[2], n[3], t);
}

function j(n, t, r, c) {
  var o = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : p();
  return o[0] = n, o[1] = t, o[2] = r, o[3] = c, o;
}

function k(n, t, c) {
  return r(c, n), c[3] = t, c;
}

function v(n, t, r) {
  var c = t[0] * t[0] + t[1] * t[1] + t[2] * t[2],
      o = Math.abs(c - 1) > 1e-5 && c > 1e-12 ? 1 / Math.sqrt(c) : 1;
  return r[0] = t[0] * o, r[1] = t[1] * o, r[2] = t[2] * o, r[3] = -(r[0] * n[0] + r[1] * n[1] + r[2] * n[2]), r;
}

function y(n, t, r) {
  var c = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : p();
  var o = r[0] - t[0],
      e = r[1] - t[1],
      u = r[2] - t[2],
      i = n[0] - t[0],
      f = n[1] - t[1],
      s = n[2] - t[2],
      a = e * s - u * f,
      g = u * i - o * s,
      m = o * f - e * i,
      h = a * a + g * g + m * m,
      M = Math.abs(h - 1) > 1e-5 && h > 1e-12 ? 1 / Math.sqrt(h) : 1;
  c[0] = a * M, c[1] = g * M, c[2] = m * M, c[3] = -(c[0] * n[0] + c[1] * n[1] + c[2] * n[2]);
}

function d(n, t) {
  return l(n, t, 0, 1, 2);
}

function l(n, t, r, i, f) {
  if (n.count < 3) return !1;
  n.getVec(r, q);
  var s = i,
      a = !1;

  for (; s < n.count - 1 && !a;) {
    n.getVec(s, x), s++, a = !c(q, x);
  }

  if (!a) return !1;

  for (s = Math.max(s, f), a = !1; s < n.count && !a;) {
    n.getVec(s, S), s++, o(U, q, x), e(U, U), o(w, x, S), e(w, w), a = !c(q, S) && !c(x, S) && Math.abs(u(U, w)) < V;
  }

  return a ? (y(q, x, S, t), !0) : (0 !== r || 1 !== i || 2 !== f) && l(n, t, 0, 1, 2);
}

var V = .99619469809,
    q = a(),
    x = a(),
    S = a(),
    U = a(),
    w = a();

function z(n, t, r) {
  return n !== r && b(n, r), r[3] = -u(W(r), t), r;
}

function A(n, t) {
  return t[0] = -n[0], t[1] = -n[1], t[2] = -n[2], t[3] = -n[3], t;
}

function B(n, t, r, c) {
  return i(S, t, n), v(r, S, c);
}

function C(n, t, r, c) {
  return T(n, t, o(h.get(), r, t), Y, c);
}

function D(n, r, c) {
  return !!t(r) && T(n, r.origin, r.direction, Z, c);
}

function E(n, t, r) {
  return T(n, t.origin, t.vector, 0, r);
}

function F(n, t, r) {
  return T(n, t.origin, t.vector, 1, r);
}

function G(n, t) {
  return R(n, g(t)) - t[3] >= 0;
}

function H(n, t) {
  return R(n, g(t)) + t[3] < 0;
}

function I(n, t) {
  return R(n, t) >= 0;
}

function J(n, t) {
  return R(n, t) < 0;
}

function K(n, t) {
  var r = t[0],
      c = t[1],
      o = t[2],
      e = t[3],
      u = t[4],
      i = t[5];
  return n[0] * (n[0] > 0 ? r : e) + n[1] * (n[1] > 0 ? c : u) + n[2] * (n[2] > 0 ? o : i) + n[3] >= 0;
}

function L(n, t) {
  var r = u(W(n), t.ray.direction),
      c = -R(n, t.ray.origin);
  if (c < 0 && r >= 0) return !1;
  if (r > -1e-6 && r < 1e-6) return c > 0;
  if ((c < 0 || r < 0) && !(c < 0 && r < 0)) return !0;
  var o = c / r;
  return r > 0 ? o < t.c1 && (t.c1 = o) : o > t.c0 && (t.c0 = o), t.c0 <= t.c1;
}

function N(n, t) {
  var r = u(W(n), t.ray.direction),
      c = -R(n, t.ray.origin);
  if (r > -1e-6 && r < 1e-6) return c > 0;
  var o = c / r;
  return r > 0 ? o < t.c1 && (t.c1 = o) : o > t.c0 && (t.c0 = o), t.c0 <= t.c1;
}

function O(n, t, r) {
  var c = f(h.get(), W(n), -n[3]),
      e = P(n, o(h.get(), t, c), h.get());
  return s(r, e, c), r;
}

function P(n, t, r) {
  var c = f(h.get(), W(n), u(W(n), t));
  return o(r, t, c), r;
}

function Q(n, t) {
  return Math.abs(R(n, t));
}

function R(n, t) {
  return u(W(n), t) + n[3];
}

function T(t, r, c, o, e) {
  var i = u(W(t), c);
  if (0 === i) return !1;
  var a = -(u(W(t), r) + t[3]) / i;
  return 1 & o && (a = n(a, 0, 1)), !(!(4 & o) && a < 0 || !(8 & o) && a > 1) && (s(e, r, f(e, c, a)), !0);
}

function W(n) {
  return n;
}

var X = [0, 0, 1, 0],
    Y = 12,
    Z = 8;
export { X as UP, L as clip, N as clipInfinite, b as copy, p as create, Q as distance, d as fromManyPoints, l as fromManyPointsSampleAt, k as fromNormalAndOffset, y as fromPoints, v as fromPositionAndNormal, j as fromValues, B as fromVectorsAndPoint, C as intersectLine, E as intersectLineSegment, F as intersectLineSegmentClamp, D as intersectRay, K as isAABBFullyInside, I as isPointInside, J as isPointOutside, G as isSphereFullyInside, H as isSphereFullyOutside, A as negate, W as normal, O as projectPoint, P as projectVector, z as setOffsetFromPoint, R as signedDistance, M as wrap };